/**
 * AdminCreatorHelperPanel · iter146
 * Admin-UI für Creator-Helper-Tarif-Verwaltung:
 * - Tarif-Editor (Preise + published)
 * - Funktionsmatrix (3 Spalten × 23 Zeilen)
 * - Quoten-Editor
 * - Trial-Config
 * - Grants (kostenlose Freischaltungen)
 */
import { useEffect, useState } from "react";
import { Loader2, Save, Shield, Sparkles, Ban, Info } from "lucide-react";
import api from "@/lib/api";

const FEATURE_LABELS = {
  text_gen_marketplace: "Marketplace-Textgenerator (neutral)",
  text_gen_stx: "stilltouchedX-Textgenerator (sinnlich)",
  text_gen_social: "Social-Content-Generator",
  text_gen_bio: "Bio-Optimierung",
  text_gen_tags: "Tag-Vorschläge",
  profile_audit: "Profil-Audit + Score",
  content_score: "Content-Score",
  content_score_trend: "Score-Trend",
  coaching_wizard: "Coaching-Wizard",
  image_analysis: "Bildanalyse",
  image_optimize: "Bildoptimierung",
  image_face_blur: "Gesichtsverfremdung Bild",
  image_tattoo_removal: "Tattoo-Removal",
  image_mole_removal: "Muttermal-Removal",
  image_mark_removal: "Merkmal-Removal",
  video_face_blur: "Video-Gesichtsverfremdung",
  video_analysis: "Video-Analyse",
  video_optimize: "Video-Optimierung",
  price_suggestions: "Preisempfehlungen",
  listing_analytics: "Listing-Analytics",
  watch_analytics_basic: "Watch Analytics Basic",
  watch_analytics_full: "Watch Analytics Full",
  benchmark_extended: "Erweiterte Benchmarks",
};

export default function AdminCreatorHelperPanel() {
  const [configs, setConfigs] = useState([]);
  const [features, setFeatures] = useState([]);
  const [trialCfg, setTrialCfg] = useState(null);
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [flash, setFlash] = useState(null);
  const [newGrant, setNewGrant] = useState({ profile_id: "", plan_code: "STANDARD", days: 30, reason: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [c, t, g] = await Promise.all([
        api.get("/admin/creator-helper/configs"),
        api.get("/admin/creator-helper/trial"),
        api.get("/admin/creator-helper/grants"),
      ]);
      setConfigs(c.data?.plans || []);
      setFeatures(c.data?.feature_keys || []);
      setTrialCfg(t.data);
      setGrants(g.data?.grants || []);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updateConfig = async (planCode, patch) => {
    setBusy(`cfg-${planCode}`);
    try {
      await api.put(`/admin/creator-helper/configs/${planCode}`, patch);
      await load();
      setFlash(`${planCode} aktualisiert`); setTimeout(() => setFlash(null), 2000);
    } catch (e) {
      alert(e?.response?.data?.detail || "Update fehlgeschlagen");
    } finally { setBusy(null); }
  };

  const toggleFeature = (planCode, featKey, currentVal) => {
    const plan = configs.find((c) => c.plan_code === planCode);
    if (!plan) return;
    const nextFeatures = { ...(plan.features || {}), [featKey]: !currentVal };
    updateConfig(planCode, { features: nextFeatures });
  };

  const saveTrial = async () => {
    setBusy("trial");
    try {
      await api.put("/admin/creator-helper/trial", trialCfg);
      setFlash("Trial-Config gespeichert"); setTimeout(() => setFlash(null), 2000);
    } catch (e) {
      alert("Fehler");
    } finally { setBusy(null); }
  };

  const createGrant = async () => {
    if (!newGrant.profile_id) { alert("Profile-ID nötig"); return; }
    setBusy("grant");
    try {
      const { data } = await api.post("/admin/creator-helper/grants", newGrant);
      // iter214 · Aufgelöste User-E-Mail in der Bestätigung anzeigen,
      // damit ein Prefix-Match sichtbar dem richtigen Konto zugeordnet ist.
      const resolvedEmail = data?.resolved_user?.email;
      const resolvedId = data?.resolved_user?.user_id || newGrant.profile_id;
      setFlash(
        resolvedEmail
          ? `✓ ${data.plan_code} freigeschaltet für ${resolvedEmail} (${resolvedId.slice(0,8)}…) für ${newGrant.days ? newGrant.days + " Tage" : "unbefristet"}`
          : `✓ Freischaltung erstellt für ${resolvedId.slice(0,8)}…`
      );
      setTimeout(() => setFlash(null), 6000);
      setNewGrant({ profile_id: "", plan_code: "STANDARD", days: 30, reason: "" });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Grant fehlgeschlagen");
    } finally { setBusy(null); }
  };

  const revokeGrant = async (grantId) => {
    if (!window.confirm("Freischaltung wirklich entziehen?")) return;
    setBusy(`revoke-${grantId}`);
    try {
      await api.post(`/admin/creator-helper/grants/${grantId}/revoke`);
      await load();
    } finally { setBusy(null); }
  };

  if (loading) return <div className="p-6 text-center"><Loader2 size={20} className="animate-spin inline text-[#999]" /></div>;

  return (
    <div data-testid="admin-creator-helper" className="p-4 space-y-6 max-w-5xl">
      <div className="flex items-center gap-2">
        <Shield size={18} /> <h2 className="text-lg font-bold">Creator-Helper Verwaltung</h2>
      </div>
      {flash && <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-2 text-xs text-emerald-700">{flash}</div>}

      {/* Tarif-Preise */}
      <section>
        <h3 className="text-sm font-bold mb-2">Tarif-Preise & Kontingente</h3>
        <div className="grid grid-cols-3 gap-2">
          {configs.map((c) => (
            <div key={c.plan_code} data-testid={`admin-helper-plan-${c.plan_code}`} className="border border-[#E5E5E5] rounded-2xl p-3 space-y-2">
              <p className="text-xs font-bold uppercase mb-1">{c.plan_code}</p>
              {/* Preise */}
              <div>
                <label className="text-[10px] text-[#666]">Monat €</label>
                <input type="number" min={0} max={999} step={0.01} value={c.price_monthly_eur ?? 0}
                  onChange={(e) => setConfigs((prev) => prev.map((p) => p.plan_code === c.plan_code ? {...p, price_monthly_eur: Number(e.target.value)} : p))}
                  data-testid={`admin-helper-price-monthly-${c.plan_code}`}
                  className="w-full h-8 rounded-full border border-[#E5E5E5] px-3 text-xs" />
              </div>
              <div>
                <label className="text-[10px] text-[#666]">Jahr €</label>
                <input type="number" min={0} max={9999} step={0.01} value={c.price_yearly_eur ?? 0}
                  onChange={(e) => setConfigs((prev) => prev.map((p) => p.plan_code === c.plan_code ? {...p, price_yearly_eur: Number(e.target.value)} : p))}
                  data-testid={`admin-helper-price-yearly-${c.plan_code}`}
                  className="w-full h-8 rounded-full border border-[#E5E5E5] px-3 text-xs" />
              </div>
              {/* Quoten (P1 · iter142.1) — pro Bucket, −1 = unbegrenzt */}
              <div className="pt-2 border-t border-[#F0F0F0]">
                <p className="text-[10px] font-bold uppercase text-[#666] mb-1">Kontingente / Monat</p>
                {[
                  { k: "text_gen_monthly", label: "Textgenerierungen" },
                  { k: "image_ops_monthly", label: "Bildoperationen" },
                  { k: "image_face_blur_monthly", label: "Gesichtsverfremdungen" },
                  { k: "video_ops_monthly", label: "Videooperationen" },
                ].map((row) => (
                  <div key={row.k} className="grid grid-cols-5 gap-1 items-center mb-1">
                    <span className="col-span-3 text-[10px] text-[#666]">{row.label}</span>
                    <input type="number" min={-1} max={100000} step={1}
                      value={c.quotas?.[row.k] ?? 0}
                      onChange={(e) => {
                        const val = Number(e.target.value);
                        setConfigs((prev) => prev.map((p) => p.plan_code === c.plan_code
                          ? {...p, quotas: {...(p.quotas || {}), [row.k]: val}} : p));
                      }}
                      data-testid={`admin-helper-quota-${row.k}-${c.plan_code}`}
                      className="col-span-2 h-7 rounded-full border border-[#E5E5E5] px-2 text-[11px] text-right" />
                  </div>
                ))}
                <p className="text-[9px] text-[#999] italic">−1 = unbegrenzt (Fair Use)</p>
              </div>
              <button
                data-testid={`admin-helper-save-${c.plan_code}`}
                onClick={() => updateConfig(c.plan_code, {
                  price_monthly_eur: c.price_monthly_eur,
                  price_yearly_eur: c.price_yearly_eur,
                  quotas: c.quotas,
                })}
                disabled={busy === `cfg-${c.plan_code}`}
                className="mt-2 w-full h-8 rounded-full bg-[#0A0A0A] text-white text-xs disabled:opacity-40 flex items-center justify-center gap-1">
                <Save size={10} /> Alles speichern
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Funktionsmatrix */}
      <section>
        <h3 className="text-sm font-bold mb-2">Funktionsmatrix (23 Features × 3 Tarife)</h3>
        <div className="overflow-x-auto border border-[#E5E5E5] rounded-2xl">
          <table className="w-full text-xs">
            <thead className="bg-[#FAFAFA]">
              <tr>
                <th className="text-left p-2 font-medium text-[#666]">Feature</th>
                {configs.map((c) => (<th key={c.plan_code} className="p-2 text-center font-bold">{c.plan_code}</th>))}
              </tr>
            </thead>
            <tbody>
              {features.map((f) => (
                <tr key={f} className="border-t border-[#F0F0F0]">
                  <td className="p-2 text-[#0A0A0A]">{FEATURE_LABELS[f] || f}</td>
                  {configs.map((c) => (
                    <td key={c.plan_code} className="p-2 text-center">
                      <input type="checkbox"
                        data-testid={`admin-helper-feat-${c.plan_code}-${f}`}
                        checked={!!c.features?.[f]}
                        onChange={() => toggleFeature(c.plan_code, f, !!c.features?.[f])}
                        className="accent-[#0A0A0A]" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Trial */}
      <section>
        <h3 className="text-sm font-bold mb-2">Testphase</h3>
        {trialCfg && (
          <div className="border border-[#E5E5E5] rounded-2xl p-3 grid grid-cols-4 gap-2 items-end">
            <label className="text-xs">
              Aktiv <input type="checkbox" data-testid="admin-trial-active" checked={!!trialCfg.active} onChange={(e) => setTrialCfg({...trialCfg, active: e.target.checked})} />
            </label>
            <label className="text-xs">
              Tage
              <input type="number" min={1} max={90} data-testid="admin-trial-days" value={trialCfg.days || 7} onChange={(e) => setTrialCfg({...trialCfg, days: Number(e.target.value)})}
                className="w-full h-9 rounded-full border border-[#E5E5E5] px-3 mt-1" />
            </label>
            <label className="text-xs">
              Plan
              <select data-testid="admin-trial-plan" value={trialCfg.plan_code || "STANDARD"} onChange={(e) => setTrialCfg({...trialCfg, plan_code: e.target.value})}
                className="w-full h-9 rounded-full border border-[#E5E5E5] px-3 mt-1">
                <option>STANDARD</option><option>PREMIUM</option>
              </select>
            </label>
            <button data-testid="admin-trial-save" onClick={saveTrial} disabled={busy === "trial"} className="h-9 rounded-full bg-[#0A0A0A] text-white text-xs disabled:opacity-40 flex items-center justify-center gap-1">
              <Save size={10} /> Speichern
            </button>
          </div>
        )}
      </section>

      {/* Grants */}
      <section>
        <h3 className="text-sm font-bold mb-2">Kostenlose Freischaltungen</h3>
        <div className="border border-[#E5E5E5] rounded-2xl p-3 grid grid-cols-5 gap-2 items-end mb-3">
          <input placeholder="Profile-ID" value={newGrant.profile_id} onChange={(e) => setNewGrant({...newGrant, profile_id: e.target.value})}
            data-testid="admin-grant-profile-id"
            className="col-span-2 h-9 rounded-full border border-[#E5E5E5] px-3 text-xs" />
          <select data-testid="admin-grant-plan" value={newGrant.plan_code} onChange={(e) => setNewGrant({...newGrant, plan_code: e.target.value})}
            className="h-9 rounded-full border border-[#E5E5E5] px-3 text-xs">
            <option>STANDARD</option><option>PREMIUM</option>
          </select>
          <input type="number" min={1} max={3650} placeholder="Tage (leer = unbefristet)" value={newGrant.days || ""} onChange={(e) => setNewGrant({...newGrant, days: e.target.value ? Number(e.target.value) : null})}
            data-testid="admin-grant-days"
            className="h-9 rounded-full border border-[#E5E5E5] px-3 text-xs" />
          <button data-testid="admin-grant-create" onClick={createGrant} disabled={busy === "grant"}
            className="h-9 rounded-full bg-[#0A0A0A] text-white text-xs disabled:opacity-40">Freischalten</button>
        </div>
        <ul className="space-y-1">
          {grants.length === 0 && <p className="text-xs text-[#999]">Keine aktiven Freischaltungen.</p>}
          {grants.map((g) => (
            <li key={g.grant_id} data-testid={`admin-grant-item-${g.grant_id}`} className="flex items-center gap-2 border border-[#E5E5E5] rounded-2xl p-2 text-xs">
              <Sparkles size={12} className="text-emerald-600" />
              <div className="flex-1">
                <span className="font-bold">{g.plan_code}</span> für <code className="text-[10px]">{g.profile_id}</code>
                {g.expires_at ? <span className="text-[#666]"> · bis {new Date(g.expires_at).toLocaleDateString("de-DE")}</span> : <span className="text-emerald-600"> · unbefristet</span>}
                {g.reason && <span className="text-[#666]"> · {g.reason}</span>}
              </div>
              <button data-testid={`admin-grant-revoke-${g.grant_id}`} onClick={() => revokeGrant(g.grant_id)}
                className="h-7 px-2 rounded-full border border-red-200 text-red-600 text-[10px] hover:bg-red-50">
                <Ban size={10} className="inline" /> Entziehen
              </button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
